import React, { useState, useRef } from "react";
import {
  FileText,
  CheckCircle,
  RotateCcw,
  ArrowRight,
  Check,
  AlertTriangle,
  Upload,
  Calendar,
} from "lucide-react";

function DragDocTransitionOverlay({ transition, onConfirm, onCancel }) {
  const { doc, to } = transition;
  const isToSigned = to === "lm_signee";

  const [dateSignature, setDateSignature] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [signedPdfFile, setSignedPdfFile] = useState(null);
  const fileInputRef = useRef(null);

  const todayStr = new Date().toISOString().split("T")[0];

  const docName = (() => {
    const name = doc.emailSubject?.split(" - ")[0]?.trim();
    return !name || name.toLowerCase() === "n/a"
      ? `${doc.nom || "-"} ${doc.prenom || "-"}`
      : name;
  })();

  const isFormValid = !isToSigned || Boolean(dateSignature);

  const handleConfirm = () => {
    if (!isFormValid) return;
    onConfirm(isToSigned ? { dateSignature, signedPdfFile } : {});
  };

  return (
    <div
      className="fixed inset-0 z-[9998] flex items-center justify-center"
      style={{ backgroundColor: "rgba(0,0,0,0.55)", backdropFilter: "blur(3px)" }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">

        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-5 border-b border-slate-100">
          <div
            className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
              isToSigned ? "bg-emerald-600" : "bg-amber-500"
            }`}
          >
            {isToSigned
              ? <CheckCircle className="w-5 h-5 text-white" />
              : <RotateCcw className="w-5 h-5 text-white" />
            }
          </div>
          <div>
            <h2 className="text-base font-bold text-slate-900 leading-tight">
              {isToSigned ? "LM signée — Validation manuelle" : "Remettre en Proposition LM"}
            </h2>
            <p className="text-xs text-slate-400 mt-0.5">
              {isToSigned
                ? "Cette LM a été signée en dehors de DocuSign"
                : "Cette LM sera remise en statut proposition"}
            </p>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">

          {/* Doc info card */}
          <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 rounded-xl border border-slate-200">
            <div className="w-8 h-8 rounded-full bg-[#9B1B5D]/10 flex items-center justify-center shrink-0">
              <FileText className="w-4 h-4 text-[#9B1B5D]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-800 truncate">{docName}</p>
              <p className="text-xs text-slate-400 truncate">
                {doc.emailSubject?.split(" - ")[1]}
              </p>
            </div>
            <ArrowRight className="w-4 h-4 text-slate-400 shrink-0" />
            <span
              className={`text-xs font-medium px-2 py-1 rounded-md shrink-0 ${
                isToSigned
                  ? "bg-emerald-100 text-emerald-700"
                  : "bg-amber-100 text-amber-700"
              }`}
            >
              {isToSigned ? "LM Signée" : "Proposition LM"}
            </span>
          </div>

          {/* Warning for reopen */}
          {!isToSigned && (
            <div className="flex items-start gap-3 px-4 py-3 bg-amber-50 rounded-xl border border-amber-200">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800">
                Cette LM sera remise en statut "Proposition LM". La validation manuelle sera annulée.
              </p>
            </div>
          )}

          {/* Form for signing */}
          {isToSigned && (
            <div className="border border-slate-200 rounded-xl overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-200">
                <FileText className="w-4 h-4 text-[#9B1B5D]" />
                <span className="text-sm font-semibold text-slate-700">
                  Détails de la signature
                </span>
              </div>
              <div className="p-4 space-y-3">

                {/* Date de signature */}
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">
                    Date de signature <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                    <input
                      type="date"
                      value={dateSignature}
                      onChange={(e) => setDateSignature(e.target.value)}
                      max={todayStr}
                      className="h-10 w-full rounded-lg border border-slate-200 pl-9 pr-3 text-sm bg-white outline-none focus:border-[#9B1B5D]/40 transition-all"
                    />
                  </div>
                </div>

                {/* Upload PDF signé */}
                <div>
                  <label className="flex items-center justify-between text-xs font-semibold text-slate-500 mb-1">
                    <span>PDF signé <span className="font-normal text-slate-400">(optionnel)</span></span>
                  </label>
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className={`relative flex items-center gap-3 h-11 px-3 rounded-lg border-2 border-dashed cursor-pointer transition-colors ${
                      signedPdfFile
                        ? "border-emerald-300 bg-emerald-50"
                        : "border-slate-200 hover:border-[#9B1B5D]/40 hover:bg-slate-50"
                    }`}
                  >
                    <Upload
                      className={`w-4 h-4 shrink-0 ${
                        signedPdfFile ? "text-emerald-600" : "text-slate-400"
                      }`}
                    />
                    <span
                      className={`text-xs truncate flex-1 ${
                        signedPdfFile
                          ? "text-emerald-700 font-medium"
                          : "text-slate-400"
                      }`}
                    >
                      {signedPdfFile
                        ? signedPdfFile.name
                        : "Cliquez pour importer le PDF signé"}
                    </span>
                    {signedPdfFile && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSignedPdfFile(null);
                          if (fileInputRef.current) fileInputRef.current.value = "";
                        }}
                        className="ml-auto text-slate-400 hover:text-slate-600 shrink-0 text-lg leading-none"
                      >
                        ×
                      </button>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="application/pdf"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files[0] || null;
                      setSignedPdfFile(file);
                    }}
                  />
                </div>

              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 bg-white">
          <button
            onClick={onCancel}
            className="px-6 h-10 border border-slate-200 text-slate-600 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={handleConfirm}
            disabled={!isFormValid}
            className={`flex items-center gap-2 px-6 h-10 text-white text-sm font-medium rounded-lg shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              isToSigned
                ? "bg-emerald-600 hover:bg-emerald-700"
                : "bg-amber-500 hover:bg-amber-600"
            }`}
          >
            <Check className="w-4 h-4" />
            {isToSigned ? "Valider la signature" : "Remettre en proposition"}
          </button>
        </div>

      </div>
    </div>
  );
}

export default DragDocTransitionOverlay;
